import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import '../../styles/Dashboard.css';
import '../../styles/BlogDetail.css';

// Displays the full blog post for paid members (requires subscription)
export default function BlogDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      const apiBase = 'https://i0nzyjj5hc.execute-api.us-east-1.amazonaws.com/prod';
      const decodedSlug = decodeURIComponent(slug);
      const url = `${apiBase}/postDetail/${encodeURIComponent(decodedSlug)}`;

      try {
        // Force refresh session to ensure tokens are valid in new windows
        const session = await fetchAuthSession({ forceRefresh: true });
        const idToken = session?.tokens?.idToken?.toString();
        const accessToken = session?.tokens?.accessToken?.toString();

        const jwt = idToken ?? accessToken;
        if (!jwt) {
          console.warn('[BlogDetail] No JWT available from session');
          setError('Authentication required. Please log in.');
          setLoading(false);
          return;
        }

        const headers = {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwt}`,
        };

        const res = await fetch(url, { mode: 'cors', headers });

        if (res.status === 403) {
          console.warn('[BlogDetail] 403 received: Subscription required');
          throw new Error('Subscription required');
        }
        if (res.status === 401) {
          console.error('[BlogDetail] 401 Unauthorized even after refresh');
        }
        if (!res.ok) {
          throw new Error(`Fetch failed: ${res.status} ${res.statusText}`);
        }

        const data = await res.json();
        console.log('[BlogDetail] Response JSON:', data);
        setPost(data);
      } catch (err) {
        console.error('[BlogDetail] Error loading post:', err);
        setError(err.message || 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug]);

  if (loading) return <p>Loading...</p>;
  if (error) return <p>Error: {error}</p>;
  if (!post) return <p>Post not found.</p>;

  return (
    <article className="blog-detail-container">
      <header className="blog-header">
        <h1 className="blog-title">{post.title}</h1>
        <div className="blog-meta">
          <time className="blog-date">
            {new Date(post.date || post.updatedAt).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </time>
          {post.category && <span className="blog-category">{post.category}</span>}
        </div>
        {post.excerpt && <p className="blog-excerpt">{post.excerpt}</p>}
      </header>
      
      {post.heroImageUrl && (
        <figure className="blog-hero-figure">
          <img
            className="blog-hero"
            src={post.heroImageUrl}
            alt={post.title}
          />
        </figure>
      )}
      
      <div
        className="blog-content"
        dangerouslySetInnerHTML={{ __html: post.content }}
      />
    </article>
  );
}
